import moment from 'moment';
import { pathOr, pluck, uniq, flatten, path } from 'ramda';
import { flowForwardTariffCodes } from 'constants/tariffCodes';
import { getOfflineFees } from 'utils/exchange';
import { loyaltyFlexTariffCodes, DIRECTIONS } from 'types/classes';
import productTypes from 'constants/productTypes';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { marginTariff } from 'constants/tariffCodes';
import { types } from 'types/passengers';
import { CLASSES_OF_SERVICE_BY_TYPE } from 'constants/classOfService';
import { TIER_BENEFIT_UPGRADE_CODE } from 'constants/tierBenefit';

const getProducts = pathOr([], ['products']);

const allowedRebookExtrasTypes = [productTypes.BIKES, productTypes.LUGGAGE, productTypes.CHAMPAIGN, productTypes.EXTERNAL_TICKET];
const allowedRebookExtrasETCodes = [marginTariff.MARGIN_TRAIN, marginTariff.MARGIN_TRAIN_ER];
const allowableServicesForExtraExchange = [serviceTypeCodes.EUROSTAR, serviceTypeCodes.GREENSPEED];
const disabilityProductCodes = [types.WHEELCHAIR, types.COMPANION, types.NO_WHEELCHAIR_COMPANION];

const buildExchSegments = (selectedFare, tariffCodesObject = {}, showValidityDate = false) =>
  selectedFare
    ? selectedFare.legs.map((leg) => ({
        origin: leg.origin.uic,
        destination: leg.destination.uic,
        direction: selectedFare.direction,
        ...(showValidityDate ? { startValidityDate: leg.timing.departureDate } : { departureDate: leg.timing.departureDate }),
        serviceName: leg.serviceName,
        serviceIdentifier: leg.serviceIdentifier,
        items: buildExchangeItems(selectedFare, leg, tariffCodesObject)
      }))
    : null;

const getDirection = (outboundOfflineFee, inboundOfflineFee) => {
  if (outboundOfflineFee) {
    return 'outbound';
  }
  if (inboundOfflineFee) {
    return 'inbound';
  }

  return null;
};

const isBP = (leg) =>
  leg && CLASSES_OF_SERVICE_BY_TYPE.PREMIER.includes(leg?.classOfService?.code) && leg?.serviceType?.code !== serviceTypeCodes.GREENSPEED;

export const existingLegContainsSpecialAssistanceProduct = ({ existingTrains, direction }) => {
  const trains = direction === DIRECTIONS.OUTBOUND ? existingTrains?.outbound : existingTrains?.inbound;
  return !!trains?.products?.find(({ passenger }) => disabilityProductCodes.includes(passenger?.disabilityType));
};

export const getPassengersBeingExchanged = (outboundSelected, inboundSelected) => {
  const outboundPaxs = outboundSelected?.bookingItems?.map((bi) => bi.passengerId);
  const inboundPaxs = inboundSelected?.bookingItems?.map((bi) => bi.passengerId);

  return {
    outboundPaxs,
    inboundPaxs
  };
};

export const getExtrasBeingExchanged = (extras, passengersToExchange, direction = DIRECTIONS.OUTBOUND) => {
  const passengers = direction === DIRECTIONS.OUTBOUND ? passengersToExchange?.outboundPaxs : passengersToExchange?.inboundPaxs;

  return extras
    ?.filter((oe) => passengers?.includes(oe.passengerId))
    ?.map((paxs) => ({
      passengerId: paxs.passengerId,
      tariffCode: paxs.tariffDetails.tariffCode,
      code: paxs.code
    }));
};

export const rebookExtrasRequest = (extrasAvailability, extras) => {
  const extrasTariffs = extrasAvailability?.extras?.filter((extra) => extras.map((ex) => ex.code)?.includes(extra.code));

  return extras
    ?.filter((extra) => extrasTariffs?.find((extrasTariff) => extrasTariff.code === extra.code))
    ?.map((e) => ({
      passengerId: e.passengerId,
      tariffCode: extrasTariffs?.find((extrasTariff) => extrasTariff.code === e.code)?.id
    }));
};

export const filterRebookExtras = (rebookExtras = [], existingExtras = []) => {
  const existingExtrasMap = new Map(existingExtras.map(({ code, type }) => [code, type]));

  const filtered = rebookExtras.filter(({ id, code }) => {
    const existingExtraCode = existingExtrasMap.get(id) || existingExtrasMap.get(code);
    return (
      existingExtraCode !== 'ET' ||
      (existingExtraCode === 'ET' && allowedRebookExtrasETCodes.includes(id)) ||
      (existingExtraCode === 'ET' && allowedRebookExtrasETCodes.includes(code))
    );
  });

  return filtered;
};

export const getExtrasVariables = (selectedFare, extras) => {
  const segments = selectedFare.legs.map((l) => ({
    direction: selectedFare.direction,
    origin: l.origin.uic,
    destination: l.destination.uic,
    serviceName: l.serviceName,
    startValidityDate: l.timing.departureDate,
    startValidityTime: l.timing.departs,
    serviceIdentifier: l.serviceIdentifier
  }));

  const products = selectedFare.legs.map((leg) => getProducts(leg)).flat();
  const uniquePassengerIds = Array.from(new Set(products.map(({ passengerId }) => passengerId))).filter((passengerId) =>
    Boolean(passengerId)
  );
  const selectedExtrasToExchange = uniquePassengerIds.flatMap((passengerId) => extras?.filter((s) => s.passengerId === passengerId));

  const passengers = selectedExtrasToExchange.map((selectedExtra) => ({
    id: selectedExtra.passengerId,
    type: selectedExtra.passenger.type,
    disabilityType: selectedExtra.passenger.disabilityType
  }));

  const types = Array.from(new Set(selectedExtrasToExchange.map(({ type } = {}) => type))).filter(
    (type) => type && allowedRebookExtrasTypes.includes(type)
  );

  return {
    segments,
    passengers,
    isAftersales: true,
    types
  };
};

export const exchangingFromFlexToValue = (tariffBeingExchanged, tariffSelected) => {
  if (tariffBeingExchanged === loyaltyFlexTariffCodes.B.bucket) {
    return tariffSelected !== loyaltyFlexTariffCodes.B.bucket;
  }
  if (tariffBeingExchanged === loyaltyFlexTariffCodes.H.bucket) {
    return tariffSelected !== loyaltyFlexTariffCodes.H.bucket;
  }

  return false;
};

export const buildTariffCode = (tariffCode, originalTariffOfExchange) =>
  tariffCode?.charAt(0) === loyaltyFlexTariffCodes.B.bucket.charAt(0) || tariffCode?.charAt(0) === loyaltyFlexTariffCodes.H.bucket.charAt(0)
    ? originalTariffOfExchange?.[0] ?? tariffCode
    : tariffCode;

export const buildExchangeItems = (selectedFare, leg, tariffCodesObject) => {
  const {
    tariffCodeBeingExchangedOutbound,
    tariffCodeBeingExchangedInbound,
    tariffCodeSelectedOutbound,
    tariffCodeSelectedInbound,
    existingTrains
  } = tariffCodesObject;

  const extrasLegToExchange =
    selectedFare.direction === DIRECTIONS.OUTBOUND ? tariffCodesObject?.outboundExtras : tariffCodesObject?.inboundExtras;

  const extrasToExchange =
    extrasLegToExchange?.length > 0 && allowableServicesForExtraExchange.includes(leg?.serviceType?.code) ? extrasLegToExchange : [];

  const loyaltyUpgradesToExchange =
    existingTrains?.[selectedFare.direction]?.feeProducts?.reduce?.(
      (loyaltyUpgrades, { passengerId, code, isCancelled, tariffDetails }) => {
        if (
          !isCancelled &&
          code === TIER_BENEFIT_UPGRADE_CODE &&
          existingTrains?.[selectedFare.direction]?.classOfService?.code === selectedFare?.classOfService?.code
        ) {
          loyaltyUpgrades.push({
            passengerId,
            tariffCode: tariffDetails.tariffCode
          });
        }

        return loyaltyUpgrades;
      },
      []
    ) ?? [];

  if (!tariffCodesObject.canOverrideFaresFlexToValue) {
    const filterExtrasMarginBasedOnTrains = () => {
      if (selectedFare?.legs?.length > 1) {
        if (leg?.serviceType?.code === serviceTypeCodes.GREENSPEED)
          return extrasToExchange.filter(({ tariffCode }) => tariffCode !== marginTariff.MARGIN_TRAIN);
        return extrasToExchange;
      }

      return extrasToExchange;
    };

    return getProducts(leg)
      .map((product) => ({
        passengerId: product.passengerId,
        tariffCode: product.tariffCode
      }))
      ?.concat(filterExtrasMarginBasedOnTrains())
      ?.concat(loyaltyUpgradesToExchange);
  }

  const tariffToExchange =
    selectedFare.direction === DIRECTIONS.OUTBOUND ? tariffCodeBeingExchangedOutbound : tariffCodeBeingExchangedInbound;
  const tariffSelected = selectedFare.direction === DIRECTIONS.OUTBOUND ? tariffCodeSelectedOutbound : tariffCodeSelectedInbound;

  const isExchangingSameClassOfAccomodation = tariffToExchange?.[0]?.charAt(0) === tariffSelected?.[0]?.charAt(0);
  const bucketBeingExchanged = tariffToExchange?.[0]?.substring(0, 3);
  const bucketSelected = tariffSelected?.[0]?.substring(0, 3);

  const isExchangingFromFlexToValue = exchangingFromFlexToValue(bucketBeingExchanged, bucketSelected);

  if (isExchangingSameClassOfAccomodation && isExchangingFromFlexToValue) {
    return getProducts(leg)
      .map((product) => ({
        passengerId: product.passengerId,
        tariffCode: buildTariffCode(product.tariffCode, tariffToExchange)
      }))
      ?.concat(extrasToExchange)
      ?.concat(loyaltyUpgradesToExchange);
  }

  return getProducts(leg)
    .map((product) => ({
      passengerId: product.passengerId,
      tariffCode: product.tariffCode
    }))
    ?.concat(extrasToExchange)
    ?.concat(loyaltyUpgradesToExchange);
};

export const buildExtraRequest = ({
  data,
  overrideReason,
  isFlowForward,
  isExchangingOFETrain,
  outboundExtras = [],
  inboundExtras = [],
  isLoyaltyBooking = false
}) => {
  const {
    outboundSelectedFare,
    inboundSelectedFare,
    currency,
    reference,
    tariffCodeBeingExchangedOutbound,
    tariffCodeSelectedOutbound,
    tariffCodeBeingExchangedInbound,
    tariffCodeSelectedInbound,
    isMember
  } = data;

  const canOverrideFaresFlexToValue = isLoyaltyBooking && isMember;
  const segments = flatten(
    [outboundSelectedFare, inboundSelectedFare]
      .map((f) =>
        buildExchSegments(f, {
          tariffCodeBeingExchangedOutbound,
          tariffCodeBeingExchangedInbound,
          tariffCodeSelectedOutbound,
          tariffCodeSelectedInbound,
          canOverrideFaresFlexToValue,
          outboundExtras,
          inboundExtras
        })
      )
      .filter(Boolean)
  );

  let overrideAftersalesFees = false;

  if (isFlowForward) {
    overrideAftersalesFees = true;
  }

  return {
    bookingReference: reference,
    currency,
    overrideAftersalesFees,
    overrideBookingLimits: false,
    overrideAftersalesRules: !!overrideReason,
    segments,
    rebookReason: overrideReason,
    isFlowForward,
    isExchangingOFETrain
  };
};

export const buildESRequest = ({
  data,
  overrideReason,
  isFlowForward,
  isExchangingOFETrain,
  outboundExtras = [],
  inboundExtras = [],
  isLoyaltyBooking = false,
  isUpgradeWithPointRequest = false
}) => {
  const {
    outboundSelectedFare,
    inboundSelectedFare,
    currency,
    reference,
    tariffCodeBeingExchangedOutbound,
    tariffCodeSelectedOutbound,
    tariffCodeBeingExchangedInbound,
    tariffCodeSelectedInbound,
    isMember,
    existingTrains
  } = data;

  const canOverrideFaresFlexToValue = isLoyaltyBooking && isMember;
  const segments = flatten(
    [outboundSelectedFare, inboundSelectedFare]
      .map((f) =>
        buildExchSegments(f, {
          tariffCodeBeingExchangedOutbound,
          tariffCodeBeingExchangedInbound,
          tariffCodeSelectedOutbound,
          tariffCodeSelectedInbound,
          canOverrideFaresFlexToValue,
          outboundExtras,
          inboundExtras,
          existingTrains
        })
      )
      .filter(Boolean)
  );

  let overrideAftersalesFees = false;

  if (isFlowForward || isUpgradeWithPointRequest) {
    overrideAftersalesFees = true;
  }

  return {
    bookingReference: reference,
    currency,
    overrideAftersalesFees,
    overrideBookingLimits: false,
    overrideAftersalesRules: !!overrideReason,
    segments,
    rebookReason: overrideReason,
    isFlowForward,
    isExchangingOFETrain
  };
};

export const buildFeeRequest = ({
  data,
  overrideReason,
  isStationAgent,
  isExchangingOFETrain,
  isFlowForward,
  wasPurchasedWithSubscription,
  isThalysUser = false,
  hasSpecialLoyaltyTier = false,
  isPackageBooking = false
}) => {
  const { outboundSelectedFare, inboundSelectedFare, currency, reference, existingTrains } = data;
  const segments = [];

  if (!(isBP(existingTrains.outbound) || isBP(existingTrains.inbound)) && !isStationAgent && !isExchangingOFETrain && !overrideReason) {
    const existingOutboundHasSpecialAssistance = existingLegContainsSpecialAssistanceProduct({ existingTrains, direction: 'outbound' });
    const existingInboundHasSpecialAssistance = existingLegContainsSpecialAssistanceProduct({ existingTrains, direction: 'inbound' });

    const outboundOfflineFee = getOfflineFees({
      outbound: outboundSelectedFare,
      isSubscriptionTicket: wasPurchasedWithSubscription,
      hasSpecialAssistance: existingOutboundHasSpecialAssistance,
      isThalysUser,
      hasSpecialLoyaltyTier,
      isPackageBooking
    });

    const inboundOfflineFee = getOfflineFees({
      inbound: inboundSelectedFare,
      isSubscriptionTicket: wasPurchasedWithSubscription,
      hasSpecialAssistance: existingInboundHasSpecialAssistance,
      isThalysUser,
      hasSpecialLoyaltyTier,
      isPackageBooking
    });

    const offlineFee = outboundOfflineFee || inboundOfflineFee;

    if (offlineFee) {
      const direction = getDirection(outboundOfflineFee, inboundOfflineFee);

      segments.push({
        startValidityDate: moment().format('YYYY-MM-DD'),
        items: [
          {
            passengerId: offlineFee.passengerId,
            tariffCode: offlineFee.tariffCode
          }
        ],
        direction
      });
    }
  }

  if (isFlowForward) {
    const allSegments = flatten([outboundSelectedFare, inboundSelectedFare].map(buildExchSegments).filter(Boolean));
    const items = pathOr([], [0, 'items'], allSegments);
    const uniquePassengerIds = uniq(pluck('passengerId', items).filter(Boolean));
    const startValidityDate = path([0, 'startValidityDate'], allSegments);
    const tariffCode = flowForwardTariffCodes[currency];

    segments.push({
      startValidityDate,
      items: uniquePassengerIds.map((passengerId) => ({
        passengerId,
        tariffCode
      }))
    });
  }

  return {
    bookingReference: reference,
    currency,
    segments
  };
};

export const buildSwapRequest = ({ data, outboundExtras = [], inboundExtras = [] }) => {
  const { currency, reference, outboundSelectedFare, inboundSelectedFare } = data;

  const segments = flatten(
    [outboundSelectedFare, inboundSelectedFare]
      .map((fare) => buildExchSegments(fare, { outboundExtras, inboundExtras }, true))
      .filter(Boolean)
  );

  return {
    bookingReference: reference,
    currency,
    segments
  };
};
